/**
 * Bandholz Shipping Progress
 *
 * Upgrade-safe cart shipping gauge for Ritual 4.3.1.
 *
 * Features:
 * • Displays on the cart page and in the native cart drawer, including at 0%.
 * • Offers one fill color or 3 solid-color stages with discrete segments.
 * • Moves the Shopify Files truck SVG with actual cart progress.
 * • Uses the native cart update result when available, with an AJAX fallback.
 * • Keeps gauge mounts separate from Ritual cart products/checkout summary.
 * • Respects Reduced Motion and preserves an accessible progress label.
 *
 * Settings: sections/bandholz-cart-progress-settings.liquid (Footer group).
 * Styles: assets/bandholz-shipping-progress.css.
 * Optional cart-page block: blocks/bandholz-shipping-progress.liquid.
 *
 * Safety: custom-tag mounts prevent Ritual morphing the gauge into its
 * checkout summary; draw() only replaces children of our own mounts.
 * No Ritual vendor files are modified. Recheck cart structure and native
 * update events after theme upgrades; verify the Footer section is retained.
 *
 * -Jenny 10/14/26
 */
(() => {
  'use strict';

  const CONFIG_SELECTOR = 'script[data-bandholz-ship-meter-config]';
  const PAGE_SELECTOR = '[data-bandholz-ship-meter-mount="page"]';
  const PAGE_ROOT = '.cart-page';
  const AUTO_PAGE_CLASS = 'bandholz-shipping-progress-page-mount';
  const DRAWER_ROOT = '#cart-drawer';
  const DRAWER_MOUNT_CLASS = 'bandholz-shipping-progress-drawer-mount';
  const METER_SELECTOR = '.bandholz-ship-meter';
  const DEFAULT_TRUCK = 'https://cdn.shopify.com/s/files/1/0596/1659/2063/files/bandholz-icon-truck.svg?v=1790198205';
  const CART_CHANGE_EVENTS = ['shopify:cart:lines-update', 'shopify:cart:discount-update'];

  let config;
  let cartState;
  let updateQueued = false;
  let fetchSequence = 0;
  let cartEventSequence = 0;
  let pendingMorph = false;
  let morphFallback = null;
  // Only reattach DOM nodes our script owns; never reuse vendor <div> nodes.
  let savedPageMount = null;
  let savedDrawerMount = null;

  // CRITICAL: Never mount a generic <div> among Ritual's own cart children.
  // Its morph utility matches unkeyed siblings by tag name and can turn our
  // mount into the native checkout summary before draw() calls replaceChildren.
  // A distinct custom tag outside cart-items-component prevents this collision.
  const OWN_MOUNT_TAG = 'bandholz-ship-meter-mount';

  function validColor(value, fallback) {
    return /^#[0-9a-f]{6}$/i.test(value || '') ? value : fallback;
  }

  function readConfig() {
    const element = document.querySelector(CONFIG_SELECTOR);
    if (!element) return null;
    try {
      const raw = JSON.parse(element.textContent || '{}');
      return {
        showPage: raw.showPage === true,
        showDrawer: raw.showDrawer === true,
        usOnly: raw.usOnly === true,
        countryCode: String(raw.countryCode || ''),
        thresholdCents: Number(raw.thresholdCents),
        beforeMessage: String(raw.beforeMessage || "You're [amount] away from free luxury USA shipping!"),
        emptyMessage: String(raw.emptyMessage || 'Add [amount] to unlock free luxury USA shipping!'),
        afterMessage: String(raw.afterMessage || 'Free luxury USA shipping unlocked!'),
        gaugeLabel: String(raw.gaugeLabel || 'Progress toward free shipping'),
        fillColor: validColor(raw.fillColor, '#E5FF52'),
        fillMode: raw.fillMode === 'stages' ? 'stages' : 'single',
        stageOneColor: validColor(raw.stageOneColor, '#FF9C37'),
        stageTwoColor: validColor(raw.stageTwoColor, '#FFE14A'),
        stageThreeColor: validColor(raw.stageThreeColor, '#5DD13D'),
        segmentCount: Math.min(16, Math.max(8, Number(raw.segmentCount) || 10)),
        // Separate three-color count ensures perfectly even thirds (3/3/3,
        // 4/4/4, or 5/5/5) without changing existing single-color settings.
        stageSegmentCount: [9, 12, 15].includes(Number(raw.stageSegmentCount))
          ? Number(raw.stageSegmentCount) : 12,
        iconUrl: String(raw.iconUrl || DEFAULT_TRUCK),
        currencyCode: String(raw.currencyCode || 'USD'),
        initialCart: {
          itemCount: Number(raw.initialCart?.itemCount || 0),
          totalPriceCents: Number(raw.initialCart?.totalPriceCents || 0)
        }
      };
    } catch (error) {
      console.warn('[Bandholz shipping progress] Invalid section configuration.', error);
      return null;
    }
  }

  function iconURL() {
    try {
      const url = new URL(config.iconUrl || DEFAULT_TRUCK, window.location.href);
      return ['https:', 'http:'].includes(url.protocol) ? url.href : DEFAULT_TRUCK;
    } catch {
      return DEFAULT_TRUCK;
    }
  }

  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: config.currencyCode, currencyDisplay: 'symbol'
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  function progress() {
    const remaining = Math.max(0, config.thresholdCents - cartState.totalPriceCents);
    const percent = Math.min(100, Math.max(0, cartState.totalPriceCents * 100 / config.thresholdCents));
    // Quantize the visible stage fill to complete segments: no two colors
    // can share one segment, even when products jump directly to 60–67%.
    // Actual percentage and ARIA value remain precise; only art is stepped.
    const unlocked = remaining === 0;
    const segmentCount = config.fillMode === 'stages'
      ? config.stageSegmentCount : config.segmentCount;
    const filledSegments = unlocked ? segmentCount
      : Math.min(segmentCount, Math.floor(percent * segmentCount / 100 + 1e-9));
    let fillColor = config.fillColor;
    if (config.fillMode === 'stages') {
      // Match the truck to the leading *completed* segment. The first green
      // segment starts at 75% with the recommended 12-segment setting.
      fillColor = unlocked || filledSegments > segmentCount * 2 / 3
        ? config.stageThreeColor
        : filledSegments > segmentCount / 3 ? config.stageTwoColor
        : config.stageOneColor;
    }
    // A truly empty cart displays an unfilled bar and outlined truck. The icon
    // interior uses the theme background instead of the first-stage color.
    const empty = cartState.itemCount === 0;
    const template = empty ? config.emptyMessage
      : remaining > 0 ? config.beforeMessage : config.afterMessage;
    return {
      percent,
      segmentCount,
      filledSegments,
      fillColor,
      unlocked,
      empty,
      message: template.replaceAll('[amount]', formatMoney(remaining))
    };
  }

  function createGauge() {
    const meter = document.createElement('div');
    meter.className = 'bandholz-ship-meter';
    meter.innerHTML = `
      <p class="bandholz-ship-meter__copy" aria-live="polite" aria-atomic="true"></p>
      <div class="bandholz-ship-meter__gauge" role="progressbar" aria-valuemin="0" aria-valuemax="100">
        <div class="bandholz-ship-meter__outline" aria-hidden="true">
          <div class="bandholz-ship-meter__inside">
            <div class="bandholz-ship-meter__fill"></div>
            <div class="bandholz-ship-meter__ticks"></div>
          </div>
        </div>
        <span class="bandholz-ship-meter__marker" aria-hidden="true">
          <span class="bandholz-ship-meter__icon">
            <img class="bandholz-ship-meter__icon-fallback" alt="" width="30" height="30" decoding="async">
            <span class="bandholz-ship-meter__icon-mask"></span>
          </span>
        </span>
      </div>`;
    return meter;
  }

  function draw(mount) {
    const { percent, segmentCount, filledSegments, message, fillColor, unlocked, empty } = progress();
    let meter = mount.querySelector(`:scope > ${METER_SELECTOR}`);
    if (!meter) {
      // Manual page blocks are explicitly marked in Liquid. Auto mounts must
      // be our custom element so native products/checkout are never cleared.
      if (mount.localName !== OWN_MOUNT_TAG && !mount.matches(PAGE_SELECTOR)) return;
      meter = createGauge();
      mount.replaceChildren(meter);
    }

    const percentValue = `${percent}%`;
    meter.style.setProperty('--bz-ship-percent', percentValue);
    meter.style.setProperty('--bz-ship-fill', fillColor);
    meter.style.setProperty('--bz-ship-icon-fill', empty ? 'var(--color-background)' : fillColor);
    meter.style.setProperty('--bz-ship-stage-one', config.stageOneColor);
    meter.style.setProperty('--bz-ship-stage-two', config.stageTwoColor);
    meter.style.setProperty('--bz-ship-stage-three', config.stageThreeColor);
    meter.dataset.bandholzFillMode = config.fillMode;
    meter.dataset.bandholzUnlocked = String(unlocked);
    meter.style.setProperty('--bz-ship-segments', String(segmentCount));
    meter.style.setProperty('--bz-ship-start-fill',
      config.fillMode === 'stages' && filledSegments > 0
        ? unlocked ? config.stageThreeColor : config.stageOneColor
        : 'transparent');
    meter.style.setProperty('--bz-ship-icon', `url(${JSON.stringify(iconURL())})`);

    const fallback = meter.querySelector('.bandholz-ship-meter__icon-fallback');
    const url = iconURL();
    if (fallback && fallback.getAttribute('src') !== url) fallback.src = url;

    const ticks = meter.querySelector('.bandholz-ship-meter__ticks');
    if (ticks) {
      if (ticks.childElementCount !== segmentCount) {
        const fragment = document.createDocumentFragment();
        for (let i = 0; i < segmentCount; i += 1) {
          const tick = document.createElement('span');
          tick.className = 'bandholz-ship-meter__tick';
          fragment.append(tick);
        }
        ticks.replaceChildren(fragment);
      }
      // Each cell gets exactly one opaque stage color or remains unfilled.
      // Keeping the normal 2px dividers avoids mixed orange/yellow pixels.
      const third = segmentCount / 3;
      for (let i = 0; i < ticks.children.length; i += 1) {
        const tick = ticks.children[i];
        const color = config.fillMode !== 'stages' || i >= filledSegments
          ? '' : unlocked ? config.stageThreeColor
          : i < third ? config.stageOneColor
          : i < 2 * third ? config.stageTwoColor : config.stageThreeColor;
        if (tick.dataset.bandholzColor !== color) {
          tick.style.backgroundColor = color;
          tick.dataset.bandholzColor = color;
        }
      }
    }

    const copy = meter.querySelector('.bandholz-ship-meter__copy');
    if (copy && copy.textContent !== message) copy.textContent = message;

    const bar = meter.querySelector('.bandholz-ship-meter__gauge');
    bar?.setAttribute('aria-valuenow', String(Math.round(percent)));
    bar?.setAttribute('aria-valuetext', message);
    bar?.setAttribute('aria-label', config.gaugeLabel);
  }

  function active() {
    return config && cartState && Number.isFinite(config.thresholdCents) &&
      config.thresholdCents > 0 &&
      (!config.usOnly || config.countryCode === 'US');
  }

  function renderPage() {
    const manual = Array.from(document.querySelectorAll(PAGE_SELECTOR));
    const autoMounts = Array.from(document.querySelectorAll(`${OWN_MOUNT_TAG}.${AUTO_PAGE_CLASS}`));
    const enabled = config.showPage && active();

    if (manual.length) {
      for (const mount of autoMounts) mount.remove();
      for (const mount of manual) {
        if (enabled && mount.dataset.bandholzShipMeterEnabled !== 'false') draw(mount);
        else mount.replaceChildren();
      }
      return;
    }

    const page = document.querySelector(PAGE_ROOT);
    const productItems = page?.querySelector(':scope > .cart-page__items');
    if (!enabled || !page || !productItems) {
      for (const mount of autoMounts) mount.remove();
      return;
    }

    let mount = page.querySelector(`:scope > ${OWN_MOUNT_TAG}.${AUTO_PAGE_CLASS}`);
    if (!mount) {
      mount = savedPageMount?.localName === OWN_MOUNT_TAG ? savedPageMount : document.createElement(OWN_MOUNT_TAG);
      mount.className = AUTO_PAGE_CLASS;
      page.insertBefore(mount, productItems);
    }
    savedPageMount = mount;
    draw(mount);
  }

  function renderDrawer() {
    const drawer = document.querySelector(DRAWER_ROOT);
    if (!drawer) return;

    // SAFETY: .cart-drawer__content belongs to cart-items-component and
    // contains the native checkout summary. Never insert, remove, or
    // reparent anything there: Ritual morphs that content on each cart update.
    // Our distinct custom element lives beside cart-items-component, directly
    // under .cart-drawer__inner, after the native header.
    const inner = drawer.querySelector('.cart-drawer__inner');
    if (!inner) return;
    const mountSelector = `:scope > ${OWN_MOUNT_TAG}.${DRAWER_MOUNT_CLASS}`;
    let mount = inner.querySelector(mountSelector);

    if (!config.showDrawer || !active()) {
      mount?.remove();
      return;
    }

    const nativeCart = inner.querySelector(':scope > cart-items-component');
    if (!nativeCart) return;

    if (!mount) {
      // Only reuse our OWN custom element, never an existing <div>.
      // The latter could have been morphed into Ritual's checkout summary.
      mount = savedDrawerMount?.localName === OWN_MOUNT_TAG
        ? savedDrawerMount : document.createElement(OWN_MOUNT_TAG);
      mount.className = DRAWER_MOUNT_CLASS;
      inner.insertBefore(mount, nativeCart);
    }

    savedDrawerMount = mount;
    draw(mount);
  }

  function render() {
    if (!config || !cartState) return;
    renderPage();
    renderDrawer();
  }

  function scheduleRender() {
    if (updateQueued) return;
    updateQueued = true;
    requestAnimationFrame(() => { updateQueued = false; render(); });
  }

  // Shopify's cart event promise contains cart totals for standard add,
  // change, remove, and discount operations. Using it avoids an additional
  // /cart.js network round trip and reduces race conditions during morphing.
  function cartFromResult(result) {
    const possible = [result?.cart, result?.detail?.cart, result?.detail];
    for (const value of possible) {
      if (!value || typeof value !== 'object') continue;
      const total = Number(value.total_price);
      const count = Number(value.item_count);
      if (Number.isFinite(total) && Number.isFinite(count)) {
        return { itemCount: count, totalPriceCents: total,
          currencyCode: typeof value.currency === 'string' ? value.currency : '' };
      }
    }
    return null;
  }

  function renderAfterCartMorph() {
    pendingMorph = false;
    if (morphFallback !== null) {
      clearTimeout(morphFallback);
      morphFallback = null;
    }
    scheduleRender();
  }

  // Apply the new cart state after the corresponding Ritual section update,
  // not in the middle of it. A fallback handles callers that don't morph a
  // cart section (external apps, some discount flows, network fallbacks).
  function waitForCartMorph() {
    pendingMorph = true;
    if (morphFallback !== null) clearTimeout(morphFallback);
    morphFallback = setTimeout(renderAfterCartMorph, 300);
  }

  async function refreshCart({ deferForMorph = false } = {}) {
    const sequence = ++fetchSequence;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`cart.js returned HTTP ${response.status}`);
      const cart = await response.json();
      if (sequence !== fetchSequence) return;
      cartState = {
        itemCount: Number(cart.item_count || 0),
        totalPriceCents: Number(cart.total_price || 0)
      };
      if (cart.currency) config.currencyCode = String(cart.currency);
      if (deferForMorph) waitForCartMorph();
      else scheduleRender();
    } catch (error) {
      console.warn('[Bandholz shipping progress] Unable to refresh cart.', error);
    }
  }

  function onCartChange(event) {
    const sequence = ++cartEventSequence;
    const promise = event.promise;
    if (!promise || typeof promise.then !== 'function') {
      refreshCart({ deferForMorph: true });
      return;
    }
    promise.then(result => {
      if (sequence !== cartEventSequence) return;
      const cart = cartFromResult(result);
      if (cart) {
        // Any outstanding manual /cart.js fetch belongs to an older state.
        ++fetchSequence;
        cartState = { itemCount: cart.itemCount, totalPriceCents: cart.totalPriceCents };
        if (cart.currencyCode) config.currencyCode = cart.currencyCode;
        waitForCartMorph();
      } else {
        refreshCart({ deferForMorph: true });
      }
    }).catch(() => { /* Leave existing state untouched after failed cart actions. */ });
  }

  // Observe structural changes (including detached cart containers). During a
  // hydration/full-section morph the mutation target may no longer have a
  // #cart-drawer or .cart-page ancestor, so checking closest() alone misses
  // the final empty-cart replacement.
  const CART_STRUCTURE_SELECTOR = [
    '#cart-drawer',
    '.cart-drawer__inner',
    '.cart-drawer__content',
    '.cart-drawer__items',
    '.cart-page',
    '.cart-page__items',
    'cart-items-component',
    OWN_MOUNT_TAG,
    PAGE_SELECTOR
  ].join(', ');

  function isCartStructure(node) {
    if (!(node instanceof Element)) return false;
    return node.matches(CART_STRUCTURE_SELECTOR) ||
      Boolean(node.closest('#cart-drawer, .cart-page')) ||
      Boolean(node.querySelector(CART_STRUCTURE_SELECTOR));
  }

  function relevantMutation(mutation) {
    if (mutation.type !== 'childList') return false;
    const target = mutation.target;
    if (!(target instanceof Element)) return false;
    // Rendering the gauge itself changes child nodes (copy, segment cells).
    // Ignore those changes to avoid self-triggered renders.
    if (target.closest('.bandholz-ship-meter, .bandholz-shipping-progress-drawer-mount, .bandholz-shipping-progress-page-mount, .bandholz-shipping-progress-page-block')) return false;
    if (isCartStructure(target) && !target.matches('body, html')) return true;
    for (const node of [...mutation.addedNodes, ...mutation.removedNodes]) {
      if (isCartStructure(node)) return true;
    }
    return false;
  }

  function init() {
    config = readConfig();
    if (!config) return;
    cartState = { ...config.initialCart };
    render();

    for (const event of CART_CHANGE_EVENTS) document.addEventListener(event, onCartChange);
    document.addEventListener('cart-section:restored', refreshCart);

    const observer = new MutationObserver(mutations => {
      if (!mutations.some(relevantMutation)) return;
      // Reconcile after structural cart changes, including the optimistic
      // empty template and the later server-rendered section replacement.
      if (pendingMorph) renderAfterCartMorph();
      else scheduleRender();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('shopify:section:load', () => {
      const next = readConfig();
      if (next) { config = next; scheduleRender(); }
    });
    document.addEventListener('shopify:section:unload', () => {
      // The editor may remove a configuration section; reconcile after it settles.
      requestAnimationFrame(() => { const next = readConfig(); if (next) { config = next; scheduleRender(); } });
    });

    window.addEventListener('pageshow', event => {
      if (event.persisted || performance.getEntriesByType('navigation')[0]?.type === 'back_forward') refreshCart();
    });
  }

  // Shopify editor may re-execute this asset when refreshing the settings section.
  if (window.__bandholzShippingMeterInitialized) return;
  window.__bandholzShippingMeterInitialized = true;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
